import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Text } from 'rizzui';
import { toast } from 'sonner';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import ModalForm from '../../../../../components/forms/ModalForm';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import { OrderCampaignService } from '../../../../../services/order/orderCampaignService';
import { OrderService } from '../../../../../services/order/orderService';
import { UserService } from '../../../../../services/user/userService';

type DistributionMode = 'automatic' | 'manual';

type ContractRow = {
    id: string;
    code?: string;
    clientCompanyName?: string;
    studentName?: string;
    studentFirstName?: string;
    studentLastName?: string;
    product?: string | { id?: string; name?: string; code?: string } | null;
    currentContractStatus?: string | { id?: string; name?: string; color?: string } | null;
};

type FacilitatorOption = {
    value: string;
    label: string;
    email?: string;
    userLogin?: string;
};

interface CreateOrderCampaignModalProps {
    isOpen: boolean;
    orderId: string;
    onClose: () => void;
    onCreated: () => void | Promise<void>;
}

const getResponseRows = (payload: any): any[] => {
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
};

const getStudentLabel = (contract: ContractRow): string => {
    return [contract.studentName, contract.studentFirstName, contract.studentLastName]
        .filter(Boolean)
        .join(' ')
        .trim() || 'N/A';
};

const getClientLabel = (contract: ContractRow): string => {
    return contract.clientCompanyName || 'N/A';
};

const getProductLabel = (contract: ContractRow): string => {
    if (!contract.product) return 'N/A';
    if (typeof contract.product === 'string') return contract.product;

    const code = contract.product.code?.trim();
    const name = contract.product.name?.trim();

    if (code && name) return `${code} - ${name}`;
    return code || name || 'N/A';
};

const getCurrentStatusLabel = (contract: ContractRow): string => {
    if (!contract.currentContractStatus) return 'N/A';
    if (typeof contract.currentContractStatus === 'string') return contract.currentContractStatus;

    return contract.currentContractStatus.name?.trim() || 'N/A';
};

const CreateOrderCampaignModal: React.FC<CreateOrderCampaignModalProps> = ({
    isOpen,
    orderId,
    onClose,
    onCreated,
}) => {
    const { handleErrors } = useHandleErrors();
    const orderService = new OrderService();
    const orderCampaignService = new OrderCampaignService();
    const userService = new UserService();

    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [distributionMode, setDistributionMode] = useState<DistributionMode>('automatic');
    const [contracts, setContracts] = useState<ContractRow[]>([]);
    const [facilitators, setFacilitators] = useState<FacilitatorOption[]>([]);
    const [selectedContractIds, setSelectedContractIds] = useState<string[]>([]);
    const [selectedFacilitatorUserIds, setSelectedFacilitatorUserIds] = useState<string[]>([]);
    const [manualAssignments, setManualAssignments] = useState<Record<string, string>>({});
    const [bulkFacilitatorUserId, setBulkFacilitatorUserId] = useState<string>('');
    const [contractSearch, setContractSearch] = useState<string>('');
    const [facilitatorSearch, setFacilitatorSearch] = useState<string>('');
    const [contractsLoading, setContractsLoading] = useState<boolean>(false);
    const [facilitatorsLoading, setFacilitatorsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [hasConfiguredFacilitatorRoles, setHasConfiguredFacilitatorRoles] = useState<boolean>(true);

    const resetState = () => {
        setName('');
        setDescription('');
        setDistributionMode('automatic');
        setContracts([]);
        setFacilitators([]);
        setSelectedContractIds([]);
        setSelectedFacilitatorUserIds([]);
        setManualAssignments({});
        setBulkFacilitatorUserId('');
        setContractSearch('');
        setFacilitatorSearch('');
        setContractsLoading(false);
        setFacilitatorsLoading(false);
        setIsSaving(false);
        setHasConfiguredFacilitatorRoles(true);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const loadContracts = async () => {
        setContractsLoading(true);
        try {
            const response = await orderService.getOrderContracts({
                page: 1,
                limit: 99999,
                filter_filters: {
                    order: orderId,
                    exclude_secured_order_campaign_contracts: true,
                },
            });
            setContracts(getResponseRows(response.getResponseData()));
        } catch (error: any) {
            toast.error(error?.message || 'No se pudieron cargar los contratos del pedido');
        } finally {
            setContractsLoading(false);
        }
    };

    const loadFacilitators = async () => {
        setFacilitatorsLoading(true);
        try {
            const companyConfigResponse = await userService.getCompanyConfigurationInfo();
            const companyConfig = companyConfigResponse.getResponseData()?.data;
            const facilitatorRoleIds = Array.isArray(companyConfig?.facilitatorRoleIds)
                ? companyConfig.facilitatorRoleIds.filter(Boolean)
                : [];

            if (facilitatorRoleIds.length === 0) {
                setHasConfiguredFacilitatorRoles(false);
                setFacilitators([]);
                return;
            }

            setHasConfiguredFacilitatorRoles(true);

            const response = await userService.listUsersForSelect({
                limit: 999999,
                filter_filters: {
                    roles: facilitatorRoleIds,
                },
            });

            const users = getResponseRows(response.getResponseData());
            setFacilitators(users.map((user: any) => ({
                value: user.id,
                label: [user.name, user.lastName].filter(Boolean).join(' ').trim() || user.email || 'Sin nombre',
                email: user.email || '',
                userLogin: user.userLogin || '',
            })));
        } catch (error: any) {
            toast.error(error?.message || 'No se pudieron cargar los dinamizadores');
        } finally {
            setFacilitatorsLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        loadContracts();
        loadFacilitators();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, orderId]);

    useEffect(() => {
        setManualAssignments((current) => {
            const nextAssignments: Record<string, string> = {};

            Object.entries(current).forEach(([contractId, userId]) => {
                if (selectedContractIds.includes(contractId) && selectedFacilitatorUserIds.includes(userId)) {
                    nextAssignments[contractId] = userId;
                }
            });

            return nextAssignments;
        });
    }, [selectedContractIds, selectedFacilitatorUserIds]);

    const filteredContracts = useMemo(() => {
        const normalizedSearch = contractSearch.trim().toLowerCase();
        if (!normalizedSearch) return contracts;

        return contracts.filter((contract) => {
            const haystack = [
                contract.code,
                getClientLabel(contract),
                getStudentLabel(contract),
                getProductLabel(contract),
                getCurrentStatusLabel(contract),
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return haystack.includes(normalizedSearch);
        });
    }, [contractSearch, contracts]);

    const filteredFacilitators = useMemo(() => {
        const normalizedSearch = facilitatorSearch.trim().toLowerCase();
        if (!normalizedSearch) return facilitators;

        return facilitators.filter((facilitator) => {
            const haystack = [facilitator.label, facilitator.email, facilitator.userLogin]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return haystack.includes(normalizedSearch);
        });
    }, [facilitatorSearch, facilitators]);

    const selectedContracts = useMemo(
        () => contracts.filter((contract) => selectedContractIds.includes(contract.id)),
        [contracts, selectedContractIds]
    );

    const selectedFacilitators = useMemo(
        () => facilitators.filter((facilitator) => selectedFacilitatorUserIds.includes(facilitator.value)),
        [facilitators, selectedFacilitatorUserIds]
    );

    const selectedFacilitatorOptions = useMemo(
        () => selectedFacilitators.map((facilitator) => ({ value: facilitator.value, label: facilitator.label })),
        [selectedFacilitators]
    );

    const allFilteredContractsSelected = filteredContracts.length > 0
        && filteredContracts.every((contract) => selectedContractIds.includes(contract.id));
    const allFilteredFacilitatorsSelected = filteredFacilitators.length > 0
        && filteredFacilitators.every((facilitator) => selectedFacilitatorUserIds.includes(facilitator.value));

    const manualAssignedCount = selectedContractIds.filter((contractId) => Boolean(manualAssignments[contractId])).length;

    const facilitatorAssignmentCounts = useMemo(() => {
        return selectedFacilitators.reduce<Record<string, number>>((acc, facilitator) => {
            acc[facilitator.value] = selectedContractIds.filter((contractId) => manualAssignments[contractId] === facilitator.value).length;
            return acc;
        }, {});
    }, [manualAssignments, selectedContractIds, selectedFacilitators]);

    const toggleContractSelection = (contractId: string, checked: boolean) => {
        setSelectedContractIds((current) => checked
            ? Array.from(new Set([...current, contractId]))
            : current.filter((id) => id !== contractId)
        );
    };

    const toggleFacilitatorSelection = (userId: string, checked: boolean) => {
        setSelectedFacilitatorUserIds((current) => checked
            ? Array.from(new Set([...current, userId]))
            : current.filter((id) => id !== userId)
        );
    };

    const toggleAllFilteredContracts = (checked: boolean) => {
        const filteredIds = filteredContracts.map((contract) => contract.id);
        setSelectedContractIds((current) => {
            if (checked) {
                return Array.from(new Set([...current, ...filteredIds]));
            }

            return current.filter((id) => !filteredIds.includes(id));
        });
    };

    const toggleAllFilteredFacilitators = (checked: boolean) => {
        const filteredIds = filteredFacilitators.map((facilitator) => facilitator.value);
        setSelectedFacilitatorUserIds((current) => {
            if (checked) {
                return Array.from(new Set([...current, ...filteredIds]));
            }

            return current.filter((id) => !filteredIds.includes(id));
        });
    };

    const applyBulkAssignment = () => {
        if (!bulkFacilitatorUserId) {
            toast.error('Debes seleccionar un dinamizador para la asignación rápida');
            return;
        }

        if (selectedContractIds.length === 0) {
            toast.error('Debes seleccionar al menos un contrato');
            return;
        }

        setManualAssignments((current) => {
            const nextAssignments = { ...current };
            selectedContractIds.forEach((contractId) => {
                nextAssignments[contractId] = bulkFacilitatorUserId;
            });
            return nextAssignments;
        });
    };

    const handleCreateCampaign = async () => {
        if (!name.trim()) {
            toast.error('Debes indicar un nombre para la campaña');
            return;
        }

        if (selectedContractIds.length === 0) {
            toast.error('Debes seleccionar al menos un contrato');
            return;
        }

        if (selectedFacilitatorUserIds.length === 0) {
            toast.error('Debes seleccionar al menos un dinamizador');
            return;
        }

        if (distributionMode === 'manual') {
            const unassignedContracts = selectedContracts.filter((contract) => !manualAssignments[contract.id]);
            if (unassignedContracts.length > 0) {
                toast.error('Debes asignar un dinamizador a cada contrato seleccionado');
                return;
            }
        }

        const payload = {
            orderId,
            name: name.trim(),
            description: description.trim() || null,
            distributionMode,
            contractIds: selectedContractIds,
            facilitatorUserIds: selectedFacilitatorUserIds,
            manualAssignments: distributionMode === 'manual'
                ? selectedContractIds.map((contractId) => ({
                    contractId,
                    userId: manualAssignments[contractId],
                }))
                : [],
        };

        setIsSaving(true);
        try {
            const response = await (await orderCampaignService.createOrderCampaign(payload)).getResponseData();
            if (response.success) {
                toast.success('Campaña creada correctamente');
                await onCreated();
                handleClose();
            } else {
                handleErrors(response);
            }
        } catch (error: any) {
            toast.error(error?.message || 'No se pudo crear la campaña');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ModalForm isOpen={isOpen} onClose={handleClose} title='Crear campaña' size='xl'>
            <div className='space-y-5'>
                <div className='grid gap-4 lg:grid-cols-2'>
                    <div>
                        <label className='mb-1.5 block text-sm font-medium text-gray-700'>Nombre</label>
                        <input
                            className='h-10 w-full rounded-md border border-gray-300 px-3 text-sm'
                            placeholder='Nombre de la campaña'
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />
                    </div>
                    <div>
                        <CustomSelect
                            id='distributionMode'
                            label='Modo de reparto'
                            options={[
                                { value: 'automatic', label: 'Automático' },
                                { value: 'manual', label: 'Manual' },
                            ]}
                            value={{ value: distributionMode, label: distributionMode === 'automatic' ? 'Automático' : 'Manual' }}
                            onChange={(selected) => setDistributionMode((selected?.value || 'automatic') as DistributionMode)}
                        />
                    </div>
                </div>

                <div>
                    <label className='mb-1.5 block text-sm font-medium text-gray-700'>Descripción</label>
                    <textarea
                        className='min-h-[96px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm'
                        placeholder='Descripción'
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                    />
                </div>

                <div className='grid gap-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)]'>
                    <div className='space-y-3 rounded-lg border border-gray-200 p-4'>
                        <div className='flex items-center justify-between gap-3'>
                            <div>
                                <Text className='text-base font-semibold text-gray-900'>Contratos del pedido</Text>
                                <Text className='text-sm text-gray-500'>{selectedContractIds.length} seleccionados</Text>
                            </div>
                            <div className='w-full max-w-xs'>
                                <input
                                    type='text'
                                    className='h-9 w-full rounded-md border border-gray-300 px-3 text-sm'
                                    placeholder='Buscar contrato, cliente, alumno...'
                                    value={contractSearch}
                                    onChange={(event) => setContractSearch(event.target.value)}
                                />
                            </div>
                        </div>

                        {contracts.length === 0 && !contractsLoading ? (
                            <div className='rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700'>
                                No hay contratos disponibles en este pedido.
                            </div>
                        ) : (
                            <div className='max-h-[360px] overflow-auto rounded-md border border-gray-200'>
                                <table className='w-full min-w-[720px] text-sm'>
                                    <thead className='sticky top-0 bg-gray-50'>
                                        <tr>
                                            <th className='px-3 py-2 text-left'>
                                                <Checkbox
                                                    checked={allFilteredContractsSelected}
                                                    onChange={(event) => toggleAllFilteredContracts(event.target.checked)}
                                                />
                                            </th>
                                            <th className='px-3 py-2 text-left'>Contrato</th>
                                            <th className='px-3 py-2 text-left'>Cliente</th>
                                            <th className='px-3 py-2 text-left'>Alumno</th>
                                            <th className='px-3 py-2 text-left'>Producto</th>
                                            <th className='px-3 py-2 text-left'>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contractsLoading ? (
                                            <tr>
                                                <td colSpan={6} className='px-3 py-6 text-center text-gray-500'>
                                                    Cargando contratos...
                                                </td>
                                            </tr>
                                        ) : filteredContracts.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className='px-3 py-6 text-center text-gray-500'>
                                                    No hay contratos que coincidan con la búsqueda.
                                                </td>
                                            </tr>
                                        ) : filteredContracts.map((contract) => {
                                            const checked = selectedContractIds.includes(contract.id);

                                            return (
                                                <tr key={contract.id} className={`border-t border-gray-100 ${checked ? 'bg-sky-50/50' : 'bg-white'}`}>
                                                    <td className='px-3 py-2 align-top'>
                                                        <Checkbox
                                                            checked={checked}
                                                            onChange={(event) => toggleContractSelection(contract.id, event.target.checked)}
                                                        />
                                                    </td>
                                                    <td className='px-3 py-2 font-medium text-gray-900'>{contract.code || 'N/A'}</td>
                                                    <td className='px-3 py-2 text-gray-700'>{getClientLabel(contract)}</td>
                                                    <td className='px-3 py-2 text-gray-700'>{getStudentLabel(contract)}</td>
                                                    <td className='px-3 py-2 text-gray-700'>{getProductLabel(contract)}</td>
                                                    <td className='px-3 py-2 text-gray-700'>{getCurrentStatusLabel(contract)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className='space-y-3 rounded-lg border border-gray-200 p-4'>
                        <div className='flex items-center justify-between gap-3'>
                            <div>
                                <Text className='text-base font-semibold text-gray-900'>Dinamizadores</Text>
                                <Text className='text-sm text-gray-500'>{selectedFacilitatorUserIds.length} seleccionados</Text>
                            </div>
                            <div className='w-full max-w-xs'>
                                <input
                                    type='text'
                                    className='h-9 w-full rounded-md border border-gray-300 px-3 text-sm'
                                    placeholder='Buscar por nombre o email...'
                                    value={facilitatorSearch}
                                    onChange={(event) => setFacilitatorSearch(event.target.value)}
                                />
                            </div>
                        </div>

                        {!hasConfiguredFacilitatorRoles && (
                            <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700'>
                                No hay roles de dinamizador configurados en la empresa.
                            </div>
                        )}

                        {hasConfiguredFacilitatorRoles && facilitators.length === 0 && !facilitatorsLoading ? (
                            <div className='rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700'>
                                No se han encontrado dinamizadores activos para la configuración actual.
                            </div>
                        ) : (
                            <div className='max-h-[360px] overflow-auto rounded-md border border-gray-200'>
                                <table className='w-full min-w-[420px] text-sm'>
                                    <thead className='sticky top-0 bg-gray-50'>
                                        <tr>
                                            <th className='px-3 py-2 text-left'>
                                                <Checkbox
                                                    checked={allFilteredFacilitatorsSelected}
                                                    onChange={(event) => toggleAllFilteredFacilitators(event.target.checked)}
                                                />
                                            </th>
                                            <th className='px-3 py-2 text-left'>Dinamizador</th>
                                            <th className='px-3 py-2 text-left'>Contacto</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {facilitatorsLoading ? (
                                            <tr>
                                                <td colSpan={3} className='px-3 py-6 text-center text-gray-500'>
                                                    Cargando dinamizadores...
                                                </td>
                                            </tr>
                                        ) : filteredFacilitators.length === 0 ? (
                                            <tr>
                                                <td colSpan={3} className='px-3 py-6 text-center text-gray-500'>
                                                    No hay dinamizadores que coincidan con la búsqueda.
                                                </td>
                                            </tr>
                                        ) : filteredFacilitators.map((facilitator) => {
                                            const checked = selectedFacilitatorUserIds.includes(facilitator.value);

                                            return (
                                                <tr key={facilitator.value} className={`border-t border-gray-100 ${checked ? 'bg-sky-50/50' : 'bg-white'}`}>
                                                    <td className='px-3 py-2 align-top'>
                                                        <Checkbox
                                                            checked={checked}
                                                            onChange={(event) => toggleFacilitatorSelection(facilitator.value, event.target.checked)}
                                                        />
                                                    </td>
                                                    <td className='px-3 py-2 font-medium text-gray-900'>{facilitator.label}</td>
                                                    <td className='px-3 py-2 text-gray-700'>
                                                        <div>{facilitator.email || 'Sin email'}</div>
                                                        {facilitator.userLogin && (
                                                            <div className='text-xs text-gray-500'>{facilitator.userLogin}</div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {distributionMode === 'automatic' && (
                    <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700'>
                        El sistema repartirá los contratos seleccionados entre los dinamizadores elegidos en orden rotativo.
                    </div>
                )}

                {distributionMode === 'manual' && (
                    <div className='space-y-4 rounded-lg border border-gray-200 p-4'>
                        <div className='flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between'>
                            <div>
                                <Text className='text-base font-semibold text-gray-900'>Asignación manual</Text>
                                <Text className='text-sm text-gray-500'>
                                    {manualAssignedCount}/{selectedContractIds.length} contratos asignados
                                </Text>
                            </div>
                            <div className='grid gap-3 lg:min-w-[420px] lg:grid-cols-[minmax(0,1fr)_auto]'>
                                <CustomSelect
                                    id='bulkFacilitatorUserId'
                                    label='Asignación rápida'
                                    options={selectedFacilitatorOptions}
                                    value={selectedFacilitatorOptions.find((option) => option.value === bulkFacilitatorUserId) || null}
                                    onChange={(selected) => setBulkFacilitatorUserId(selected?.value || '')}
                                />
                                <Button
                                    variant='outline'
                                    className='h-10'
                                    onClick={applyBulkAssignment}
                                    disabled={selectedContractIds.length === 0 || selectedFacilitatorOptions.length === 0}
                                >
                                    Aplicar a todos
                                </Button>
                            </div>
                        </div>

                        {selectedFacilitators.length > 0 && (
                            <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-3'>
                                {selectedFacilitators.map((facilitator) => {
                                    const assignedContracts = facilitatorAssignmentCounts[facilitator.value] || 0;

                                    return (
                                        <div
                                            key={facilitator.value}
                                            className={`rounded-md border px-3 py-2 ${assignedContracts > 0 ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}
                                        >
                                            <Text className='text-sm font-semibold text-gray-900'>{facilitator.label}</Text>
                                            <Text className='text-xs text-gray-600'>{assignedContracts} contrato/s asignado/s</Text>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {selectedContractIds.length === 0 || selectedFacilitatorUserIds.length === 0 ? (
                            <div className='rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700'>
                                Selecciona al menos un contrato y un dinamizador para preparar la asignación manual.
                            </div>
                        ) : (
                            <div className='max-h-[320px] overflow-auto rounded-md border border-gray-200'>
                                <table className='w-full min-w-[760px] text-sm'>
                                    <thead className='sticky top-0 bg-gray-50'>
                                        <tr>
                                            <th className='px-3 py-2 text-left'>Contrato</th>
                                            <th className='px-3 py-2 text-left'>Cliente</th>
                                            <th className='px-3 py-2 text-left'>Alumno</th>
                                            <th className='px-3 py-2 text-left'>Dinamizador asignado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedContracts.map((contract) => (
                                            <tr key={contract.id} className='border-t border-gray-100'>
                                                <td className='px-3 py-2 font-medium text-gray-900'>{contract.code || 'N/A'}</td>
                                                <td className='px-3 py-2 text-gray-700'>{getClientLabel(contract)}</td>
                                                <td className='px-3 py-2 text-gray-700'>{getStudentLabel(contract)}</td>
                                                <td className='px-3 py-2'>
                                                    <CustomSelect
                                                        id={`manual-assignment-${contract.id}`}
                                                        options={selectedFacilitatorOptions}
                                                        value={selectedFacilitatorOptions.find((option) => option.value === manualAssignments[contract.id]) || null}
                                                        onChange={(selected) => setManualAssignments((current) => ({
                                                            ...current,
                                                            [contract.id]: selected?.value || '',
                                                        }))}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                <div className='flex justify-end gap-2'>
                    <Button variant='outline' onClick={handleClose} disabled={isSaving}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleCreateCampaign}
                        isLoading={isSaving}
                        disabled={isSaving || contractsLoading || facilitatorsLoading || !hasConfiguredFacilitatorRoles}
                    >
                        Crear campaña
                    </Button>
                </div>
            </div>
        </ModalForm>
    );
};

export default CreateOrderCampaignModal;
